/**
 * Template Management Actions
 * NgRx actions for admin template state management
 */
import { createAction, props } from '@ngrx/store';
import {
  Template,
  TemplateStats,
  TemplatesResponse,
  TemplateFilters,
  TemplateDetailsResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  CloneTemplateRequest,
  JsonValidationResponse,
  TemplateAIConfig
} from '../../models/template.models';

// ==================== Load Templates ====================
export const loadTemplates = createAction(
  '[Templates] Load Templates',
  props<{ filters?: TemplateFilters; page?: number; pageSize?: number }>()
);

export const loadTemplatesSuccess = createAction(
  '[Templates] Load Templates Success',
  props<{ response: TemplatesResponse }>()
);

export const loadTemplatesFailure = createAction(
  '[Templates] Load Templates Failure',
  props<{ error: string }>()
);

// ==================== Load Template Stats ====================
export const loadTemplateStats = createAction('[Templates] Load Template Stats');

export const loadTemplateStatsSuccess = createAction(
  '[Templates] Load Template Stats Success',
  props<{ stats: TemplateStats }>()
);

export const loadTemplateStatsFailure = createAction(
  '[Templates] Load Template Stats Failure',
  props<{ error: string }>()
);

// ==================== Load Single Template ====================
export const loadTemplate = createAction(
  '[Templates] Load Template',
  props<{ templateId: string }>()
);

export const loadTemplateSuccess = createAction(
  '[Templates] Load Template Success',
  props<{ response: TemplateDetailsResponse }>()
);

export const loadTemplateFailure = createAction(
  '[Templates] Load Template Failure',
  props<{ error: string }>()
);

// ==================== Create Template ====================
export const createTemplate = createAction(
  '[Templates] Create Template',
  props<{ request: CreateTemplateRequest }>()
);

export const createTemplateSuccess = createAction(
  '[Templates] Create Template Success',
  props<{ template: Template }>()
);

export const createTemplateFailure = createAction(
  '[Templates] Create Template Failure',
  props<{ error: string }>()
);

// ==================== Update Template ====================
export const updateTemplate = createAction(
  '[Templates] Update Template',
  props<{ templateId: string; request: UpdateTemplateRequest }>()
);

export const updateTemplateSuccess = createAction(
  '[Templates] Update Template Success',
  props<{ template: Template }>()
);

export const updateTemplateFailure = createAction(
  '[Templates] Update Template Failure',
  props<{ error: string }>()
);

// ==================== Delete Template ====================
export const deleteTemplate = createAction(
  '[Templates] Delete Template',
  props<{ templateId: string; hardDelete?: boolean }>()
);

export const deleteTemplateSuccess = createAction(
  '[Templates] Delete Template Success',
  props<{ templateId: string; message: string }>()
);

export const deleteTemplateFailure = createAction(
  '[Templates] Delete Template Failure',
  props<{ error: string }>()
);

// ==================== Clone Template ====================
export const cloneTemplate = createAction(
  '[Templates] Clone Template',
  props<{ templateId: string; request: CloneTemplateRequest }>()
);

export const cloneTemplateSuccess = createAction(
  '[Templates] Clone Template Success',
  props<{ template: Template }>()
);

export const cloneTemplateFailure = createAction(
  '[Templates] Clone Template Failure',
  props<{ error: string }>()
);

// ==================== Approve/Reject Template ====================
export const approveTemplate = createAction(
  '[Templates] Approve Template',
  props<{ templateId: string; reason?: string; aiConfig?: TemplateAIConfig }>()
);

export const rejectTemplate = createAction(
  '[Templates] Reject Template',
  props<{ templateId: string; reason: string }>()
);

export const approvalSuccess = createAction(
  '[Templates] Approval Success',
  props<{ template: Template; message: string }>()
);

export const approvalFailure = createAction(
  '[Templates] Approval Failure',
  props<{ error: string }>()
);

// ==================== Validate JSON ====================
export const validateJson = createAction(
  '[Templates] Validate JSON',
  props<{ jsonContent: string }>()
);

export const validateJsonSuccess = createAction(
  '[Templates] Validate JSON Success',
  props<{ response: JsonValidationResponse }>()
);

export const validateJsonFailure = createAction(
  '[Templates] Validate JSON Failure',
  props<{ error: string }>()
);

// ==================== UI State ====================
export const selectTemplate = createAction(
  '[Templates] Select Template',
  props<{ templateId: string | null }>()
);

export const setFilters = createAction(
  '[Templates] Set Filters',
  props<{ filters: TemplateFilters }>()
);

export const clearError = createAction('[Templates] Clear Error');
export const clearValidation = createAction('[Templates] Clear Validation');
export const clearSelectedTemplate = createAction('[Templates] Clear Selected Template');

